import React, { useState } from 'react';
import { Box, Typography, TextField, Button, Stack, MenuItem, Alert, Paper } from '@mui/material';
import axiosInstance from '../axiosInstance';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';

const CreateNotification: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [notification, setNotification] = useState({
    title: '',
    message: '',
    type: 'info' as 'error' | 'warning' | 'info' | 'success'
  });

  const [status, setStatus] = useState<{
    show: boolean;
    type: 'success' | 'error';
    message: string;
  }>({ show: false, type: 'success', message: '' });

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'error':
        return theme.palette.error.main;
      case 'warning':
        return theme.palette.warning.main;
      case 'success':
        return theme.palette.success.main;
      case 'info':
        return theme.palette.info.main;
      default:
        return undefined;
    }
  };

  const handleCreateNotification = async () => {
    try {
      const params = new URLSearchParams({
        message: notification.message,
        title: notification.title,
        type: notification.type
      });

      await axiosInstance.post(`/websockets/notifications/?${params}`);
      
      setStatus({
        show: true,
        type: 'success',
        message: 'Powiadomienie zostało utworzone pomyślnie'
      });

      setNotification({
        title: '',
        message: '',
        type: 'info'
      });

      setTimeout(() => {
        setStatus(prev => ({ ...prev, show: false }));
        navigate('/notifications');
      }, 3000);
    } catch (error) {
      setStatus({
        show: true,
        type: 'error',
        message: 'Wystąpił błąd podczas tworzenia powiadomienia'
      });
    }
  };

  return (
    <Box sx={{ 
      p: { xs: 1, sm: 2, md: 3 }, 
      width: '100%', 
      height: '100vh',
      overflow: 'hidden',
      bgcolor: theme.name === 'dark' ? 'background.default' : 'inherit'
    }}>
      <Paper elevation={3} sx={{ 
        p: { xs: 2, sm: 2, md: 3 }, 
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        bgcolor: theme.name === 'dark' ? 'background.paper' : 'inherit'
      }}>
        <Box sx={{ 
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3
        }}>
          <Typography variant="h4" sx={{ fontWeight: 500 }}>
            Nowe powiadomienie
          </Typography>
          <Button
            variant="outlined"
            onClick={() => navigate('/notifications')}
          >
            Powrót do listy
          </Button>
        </Box>
        
        <Stack spacing={4} sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          flex: 1, 
          overflow: 'visible'
        }}>
          <TextField
            label="Tytuł"
            fullWidth
            value={notification.title}
            onChange={(e) => setNotification(prev => ({ ...prev, title: e.target.value }))}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: '1.1rem',
              }
            }}
          />
          
          <Box sx={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column',
            minHeight: '300px',
            mb: 2
          }}>
            <TextField
              label="Wiadomość"
              fullWidth
              multiline
              rows={12}
              value={notification.message}
              onChange={(e) => setNotification(prev => ({ ...prev, message: e.target.value }))}
              variant="outlined"
              sx={{
                flex: 1,
                display: 'flex',
                height: '100%',
                '& .MuiInputBase-root': {
                  height: '100%',
                },
                '& .MuiOutlinedInput-root': {
                  height: '100%',
                },
                '& .MuiOutlinedInput-inputMultiline': {
                  height: '100% !important',
                  overflow: 'auto',
                },
                '& textarea': {
                  height: '100% !important',
                  overflow: 'auto !important',
                  fontSize: '1.1rem',
                }
              }}
            />
          </Box>
          
          <TextField
            select
            label="Typ"
            value={notification.type}
            onChange={(e) => setNotification(prev => ({ 
              ...prev, 
              type: e.target.value as 'error' | 'warning' | 'info' | 'success' 
            }))}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: '1.1rem',
              }
            }}
          >
            <MenuItem 
              value="success"
              sx={{ 
                color: 'success.main',
                fontWeight: notification.type === 'success' ? 'bold' : 'normal',
                '&:hover': {
                  bgcolor: 'success.lighter',
                }
              }}
            >
              Sukces
            </MenuItem>
            <MenuItem 
              value="error"
              sx={{ 
                color: 'error.main',
                fontWeight: notification.type === 'error' ? 'bold' : 'normal',
                '&:hover': {
                  bgcolor: 'error.lighter',
                }
              }}
            >
              Błąd
            </MenuItem>
            <MenuItem 
              value="warning"
              sx={{ 
                color: 'warning.main',
                fontWeight: notification.type === 'warning' ? 'bold' : 'normal',
                '&:hover': {
                  bgcolor: 'warning.lighter',
                }
              }}
            >
              Ostrzeżenie
            </MenuItem>
            <MenuItem 
              value="info"
              sx={{ 
                color: 'info.main',
                fontWeight: notification.type === 'info' ? 'bold' : 'normal',
                '&:hover': {
                  bgcolor: 'info.lighter',
                }
              }}
            >
              Informacja
            </MenuItem>
          </TextField>

          {status.show && (
            <Alert 
              severity={status.type}
              sx={{ 
                fontSize: '1.1rem',
                '& .MuiAlert-message': { flex: 1 }
              }}
            >
              {status.message}
            </Alert>
          )}
          
          <Box sx={{ mt: 2 }}>
            <Button 
              variant="contained" 
              onClick={handleCreateNotification}
              disabled={!notification.title || !notification.message}
              sx={{ 
                py: 1.5,
                fontSize: '1.1rem',
                bgcolor: getTypeColor(notification.type),
                '&:hover': {
                  bgcolor: theme.palette[notification.type].dark
                },
                '&.Mui-disabled': {
                  bgcolor: theme.palette.action.disabledBackground
                }
              }}
            >
              Wyślij powiadomienie
            </Button>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
};

export default CreateNotification; 